import React from "react";
import styled from "styled-components";
import Layout from "../components/layout";
import Seo from "../components/seo";
import { StaticImage } from "gatsby-plugin-image";
import {
  SectionWrapper,
  SocialLinkWrapper,
  HeadingText,
} from "../components/common";
import now from "../content/now.json";

const HomePageContent = styled(SectionWrapper)`
  padding: 7em 3em 5em;
  text-align: center;

  @media (min-width: 1200px) {
    padding: 7em 4em 5em;
  }

  @media (min-width: 1500px) {
    padding: 7em 6em 5em;
  }

  @media (max-width: 768px) {
    padding: 4em 1.5em;
  }
`;

const Lead = styled.p`
  text-align: center;
  max-width: 880px;
  margin: 0 auto 2.5em;
  font-size: 1.3em !important;
  color: #cfcfcf;
`;

const BodyBlock = styled.div`
  max-width: 860px;
  margin: 0 auto;
  text-align: left;

  p {
    margin-bottom: 1.2em;
    font-size: 1.15em;
  }
`;

const NowSection = styled.section`
  margin: 3.5em auto 0;
  max-width: 860px;
  padding-top: 2em;
  border-top: 1px solid #2a2a2a;
  text-align: left;

  h2 {
    font-size: 2em;
    margin-bottom: 0.2em;
    background: ${({ theme }) => theme.colors.secondaryGradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .now-updated {
    font-size: 0.85em;
    color: #888;
    margin-bottom: 1.5em;
    font-style: italic;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    font-weight: 300;
    font-size: 1.05em;
    line-height: 1.6em;
    margin-bottom: 1em;
    padding-left: 1.2em;
    position: relative;
  }

  li::before {
    content: "→";
    position: absolute;
    left: 0;
    color: #f17325;
  }

  li a {
    color: #f2994a;
    text-decoration: none;
    margin-left: 6px;
  }

  li a:hover {
    text-decoration: underline;
  }
`;

const SocialLinks = styled.div`
  margin-top: 3em;
  text-align: center;
`;

const SocialLink = ({ label, url, ...otherProps }) => (
  <SocialLinkWrapper>
    <a href={url} {...otherProps}>
      {label}
    </a>
  </SocialLinkWrapper>
);

const UserImageWrapper = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    margin-bottom: 2em;
  }
`;

const IndexPage = () => {
  return (
    <Layout>
      <Seo
        title="Intro"
        description="Backend engineer and solutions architect based in Lagos. Building payment infrastructure, leading the banking and payments engineering arm at 9jaPay, heading engineering at Canalis Labs, and open-sourcing Ojuri — a multi-agent fraud detection platform."
      />
      <HomePageContent>
        <UserImageWrapper>
          <StaticImage
            src={"../images/profile.png"}
            alt={" Ayodeji Abodunrin"}
            layout="constrained"
            height={120}
            quality={100}
          />
        </UserImageWrapper>
        <HeadingText>
          Hi, <span>I'm Ayo!</span>
        </HeadingText>
        <Lead>
          Backend engineer and solutions architect based in Lagos.
        </Lead>
        <BodyBlock>
          <p>
            I architect and build the engineering behind financial systems
            people rely on every day. Today, I lead the banking and payments
            engineering arm at 9jaPay (a Nigerian fintech) and head engineering
            at Canalis Labs (a multi-practice technology and transformation
            firm). In the open, I build Ojuri, a multi-agent fraud detection
            platform for mobile money and payment systems.
          </p>
          <p>
            Over the past 8+ years I've designed high-throughput payment
            platforms, built ledger and reconciliation systems for multi-tenant
            banking, and led cross-functional teams delivering enterprise
            insurance platforms across Life, General, and Health product lines.
          </p>
          <p>
            Stack I live in: Node.js, TypeScript, .NET/C#, PostgreSQL, GraphQL,
            Kafka, Google Pub/Sub, AWS.
          </p>
          <p>
            Recently completed an M.Sc. in Computer Science at the University
            of East London, on top of a Master's in Information Technology from
            the University of Lagos and a BSc in Computer Science.
          </p>
          <p>Got something interesting to build? Let's talk.</p>
        </BodyBlock>

        <NowSection>
          <h2>Now</h2>
          <p className="now-updated">Updated {now.updated}</p>
          <ul>
            {now.items.map((item, index) => (
              <li key={index}>
                {item.text}
                {item.link && (
                  <a href={item.link.href} target="_blank" rel="noreferrer">
                    {item.link.label}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </NowSection>

        <SocialLinks>
          <SocialLink
            label="Email"
            target="_blank"
            url="mailto:aboayosam@gmail.com"
          />
          <SocialLink
            label="LinkedIn"
            target="_blank"
            url="https://www.linkedin.com/in/abodunrinayodeji/"
          />
          <SocialLink
            label="GitHub"
            target="_blank"
            url="https://github.com/omob"
          />
        </SocialLinks>
      </HomePageContent>
    </Layout>
  );
};

export default IndexPage;
